import { authGet, authPost } from '../http_client.js';
import type {
  EntitlementDto,
  ClaimFreeOfferResponse,
  OfferEventType,
  OfferTriggerEventRequest,
  OfferTriggerEventResponse,
  OffersResponse,
  TelegramStarsInvoiceRequest,
  TelegramStarsInvoiceResponse,
  TelegramStarsOrderResponse,
  TonInvoiceRequest,
  TonInvoiceResponse,
  TonOrderResponse,
  TonTransactionResponse,
  ValidatePurchaseRequest,
  ValidatePurchaseResponse,
} from '../dto/offers.dto.js';

export async function getOffers( session_id?: string ): Promise<OffersResponse>
{
  // locked_offers=1: this build renders a spent cycle limit as a locked card
  // with a countdown, so the server may keep such offers in the catalog.
  const params = new URLSearchParams( { locked_offers: '1' } );
  if ( session_id ) params.set( 'session_id', session_id );
  return authGet<OffersResponse>( `api/player/me/offers?${params.toString()}` );
}

export async function getEntitlements(): Promise<Record<string, EntitlementDto>>
{
  return authGet<Record<string, EntitlementDto>>( 'api/player/me/offers/entitlements' );
}

export async function validateOfferPurchase( offer_id: string, payload: ValidatePurchaseRequest ): Promise<ValidatePurchaseResponse>
{
  return authPost<ValidatePurchaseResponse>( `api/player/me/offers/${encodeURIComponent( offer_id )}/validate-purchase`, payload, 30_000 );
}

export async function createTelegramStarsInvoice( offer_id: string, payload: TelegramStarsInvoiceRequest ): Promise<TelegramStarsInvoiceResponse>
{
  return authPost<TelegramStarsInvoiceResponse>( `api/player/me/offers/${encodeURIComponent( offer_id )}/telegram-stars/invoice`, payload, 20_000 );
}

export async function getTelegramStarsOrder( order_id: string ): Promise<TelegramStarsOrderResponse>
{
  return authGet<TelegramStarsOrderResponse>( `api/player/me/offers/telegram-stars/orders/${encodeURIComponent( order_id )}`, 15_000 );
}

// TON / USDT through TON Connect: the server issues the order and the exact transaction to sign.
export async function createTonInvoice( offer_id: string, payload: TonInvoiceRequest ): Promise<TonInvoiceResponse>
{
  return authPost<TonInvoiceResponse>( `api/player/me/offers/${encodeURIComponent( offer_id )}/ton/invoice`, payload, 20_000 );
}

export async function getTonOrder( order_id: string ): Promise<TonOrderResponse>
{
  return authGet<TonOrderResponse>( `api/player/me/offers/ton/orders/${encodeURIComponent( order_id )}`, 15_000 );
}

/** A fresh signing window (and the current wallet) for an existing order. */
export async function getTonOrderTransaction( order_id: string, sender_address?: string | null ): Promise<TonTransactionResponse>
{
  // The wallet address goes in the body, never the query string (access logs).
  return authPost<TonTransactionResponse>( `api/player/me/offers/ton/orders/${encodeURIComponent( order_id )}/transaction`, sender_address ? { sender_address } : {}, 15_000 );
}

/** The signed message TON Connect returned; a hint for support, never a proof of payment. */
export async function markTonOrderSent( order_id: string, boc: string ): Promise<TonOrderResponse>
{
  return authPost<TonOrderResponse>( `api/player/me/offers/ton/orders/${encodeURIComponent( order_id )}/sent`, { boc }, 15_000 );
}

export async function ackTonOrder( order_id: string ): Promise<{ ok: boolean; acknowledged: boolean }>
{
  return authPost<{ ok: boolean; acknowledged: boolean }>( `api/player/me/offers/ton/orders/${encodeURIComponent( order_id )}/ack`, {}, 10_000 );
}

export async function sendOfferEvent(
  offer_id: string,
  event_type: OfferEventType,
  context?: Record<string, unknown>,
): Promise<{ ok: boolean }>
{
  return authPost<{ ok: boolean }>( `api/player/me/offers/${encodeURIComponent( offer_id )}/events`, {
    event_type,
    context,
  } );
}

export async function claimFreeOffer(
  offer_id: string,
  claim_id: string,
  trigger_event_id?: string,
  session_id?: string,
  placement_id?: string,
  rewarded?: { ad_id: string | null },
): Promise<ClaimFreeOfferResponse>
{
  return authPost<ClaimFreeOfferResponse>(
    `api/player/me/offers/${encodeURIComponent( offer_id )}/claim-free`,
    {
      claim_id,
      ...( trigger_event_id ? { trigger_event_id } : {} ),
      ...( placement_id ? { placement_id } : {} ),
      ...( session_id ? { session_id } : {} ),
      ...( rewarded ? { rewarded: true, ...( rewarded.ad_id ? { rewarded_ad_id: rewarded.ad_id } : {} ) } : {} ),
    },
  );
}

export async function reportOfferTriggerEvent(
  payload: OfferTriggerEventRequest,
): Promise<OfferTriggerEventResponse>
{
  return authPost<OfferTriggerEventResponse>( 'api/player/me/offers/trigger-events', payload );
}
